/**
 * ITER308 · Phase A — Asset Detail (MRO record).
 *
 * Every asset gets an aircraft-maintenance-record-grade detail page:
 *   • Header strip     — asset tag (monospace), name, health pill,
 *                         category, outlet, location.
 *   • Key dates row    — installed / warranty / AMC / last service
 *                         with days-remaining countdowns (amber ≤30d,
 *                         red past-due).
 *   • Tabs             — Overview | Service History | Inspections
 *                         | PM Schedule | Documents | Cost.
 *
 * Phase A implements Overview and Service History fully. Inspections,
 * PM Schedule, and Cost are stubbed with designed empty states —
 * Phase B & C build them out.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, Wrench, ClipboardList, Calendar, FileText, DollarSign,
  Plus, Info, Shield, Building2, MapPin, Download,
} from 'lucide-react';

const HEALTH_STYLES = {
  healthy:         { label: 'Healthy',         cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  needs_attention: { label: 'Needs Attention', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  critical:        { label: 'Critical',        cls: 'bg-red-50 text-red-800 border-red-200' },
  out_of_service:  { label: 'Out of Service',  cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

const ENTRY_TYPE = {
  pm:                 { label: 'Preventive Maintenance', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  breakdown:          { label: 'Breakdown',              tone: 'bg-red-50 text-red-800 border-red-200' },
  inspection_failure: { label: 'Inspection Failure',     tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  ad_hoc:             { label: 'Ad-hoc Service',         tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  amendment:          { label: 'Amendment',              tone: 'bg-blue-50 text-blue-800 border-blue-200' },
};


async function exportServiceRecordPdf(API, getHeaders, assetId, asset) {
  try {
    const res = await axios.get(
      `${API}/admin/maintenance/assets/${assetId}/service-record.pdf`,
      { headers: getHeaders(), responseType: 'blob' },
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service-record-${asset?.asset_tag || assetId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Service record downloaded');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'PDF export failed');
  }
}

function HeadingBand({ title, action }) {
  return (
    <div className="rounded-lg mb-4 overflow-hidden border border-[color:var(--color-primary)]/30">
      <div className="flex items-center justify-between px-4 py-2.5 gap-3"
           style={{ background: 'var(--color-primary)', color: 'white' }}>
        <div className="text-sm font-semibold tracking-wide">{title}</div>
        {action}
      </div>
    </div>
  );
}

// ITER308 · Every date shows both an absolute value and a relative
// countdown. Amber ≤30 days, red past-due. Airline logbook standard.
function DateWithCountdown({ label, iso }) {
  if (!iso) {
    return (
      <div className="space-y-0.5">
        <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">{label}</div>
        <div className="text-sm text-[#5C6B64]">—</div>
      </div>
    );
  }
  const date = new Date(iso);
  const now = new Date();
  const days = Math.round((date - now) / 86400000);
  const isPast = days < 0;
  const soon = !isPast && days <= 30;
  const tone = isPast
    ? 'text-red-700'
    : soon
      ? 'text-amber-700'
      : 'text-[#1A2421]';
  const rel = isPast
    ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`
    : days === 0
      ? 'today'
      : `in ${days} day${days === 1 ? '' : 's'}`;
  const abs = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold">{label}</div>
      <div className={`text-sm font-medium ${tone}`}>{abs}</div>
      <div className={`text-[11px] ${tone} opacity-80`}>{rel}</div>
    </div>
  );
}

export default function AssetDetail() {
  const { getHeaders, API } = useAuth();
  const navigate = useNavigate();
  const { assetId } = useParams();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, hRes] = await Promise.all([
        axios.get(`${API}/admin/maintenance/assets/${assetId}`, { headers: getHeaders() }),
        axios.get(`${API}/admin/maintenance/assets/${assetId}/service-history`, { headers: getHeaders() }),
      ]);
      setAsset(aRes.data);
      setHistory(hRes.data);
    } catch (err) {
      toast.error('Failed to load asset');
      navigate('/admin/maintenance');
    } finally { setLoading(false); }
  }, [API, assetId, getHeaders, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-[#5C6B64] py-12 text-center">Loading asset…</div>;
  if (!asset) return null;

  const h = HEALTH_STYLES[asset.health] || HEALTH_STYLES.healthy;

  return (
    <div data-testid="asset-detail-page">
      <div className="flex items-center gap-2 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/maintenance')}
                className="text-[#5C6B64]" data-testid="asset-back-btn">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to register
        </Button>
      </div>

      {/* ── Header strip ─────────────────────────── */}
      <Card className="border-[#E2E8DF] shadow-sm mb-4"
             style={{ background: 'linear-gradient(180deg, #F9F9F8 0%, #FFFFFF 100%)' }}>
        <CardContent className="py-4 px-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[15px] text-[color:var(--color-primary)] font-bold tracking-wider"
                       data-testid="asset-detail-tag">
                  {asset.asset_tag}
                </span>
                <Badge className={`text-[10px] px-1.5 py-0.5 border ${h.cls}`}
                       data-testid="asset-detail-health">{h.label}</Badge>
                <Badge variant="outline" className="text-[10px] text-[#5C6B64] border-[#E2E8DF]">
                  {asset.status}
                </Badge>
              </div>
              <h2 className="text-lg font-semibold text-[#1A2421] mt-1">{asset.name}</h2>
              <div className="flex items-center gap-3 text-[12px] text-[#5C6B64] mt-1 flex-wrap">
                <span className="inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {asset.category_name}</span>
                <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {asset.outlet_name}</span>
                {asset.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {asset.location}</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              <DateWithCountdown label="Installed"       iso={asset.installation_date} />
              <DateWithCountdown label="Warranty"        iso={asset.warranty_expiry} />
              <DateWithCountdown label="AMC Expiry"      iso={asset.amc_expiry} />
              <DateWithCountdown label="Last Service"    iso={asset.last_service_at} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#F3F5F2] mb-4">
          <TabsTrigger value="overview"    className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-overview">
            <Info className="w-3.5 h-3.5 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="service"     className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-service">
            <Wrench className="w-3.5 h-3.5 mr-1" /> Service History
          </TabsTrigger>
          <TabsTrigger value="inspections" className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-inspections">
            <Shield className="w-3.5 h-3.5 mr-1" /> Inspections
          </TabsTrigger>
          <TabsTrigger value="pm"          className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-pm">
            <Calendar className="w-3.5 h-3.5 mr-1" /> PM Schedule
          </TabsTrigger>
          <TabsTrigger value="docs"        className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-docs">
            <FileText className="w-3.5 h-3.5 mr-1" /> Documents
          </TabsTrigger>
          <TabsTrigger value="cost"        className="data-[state=active]:bg-[color:var(--color-primary)] data-[state=active]:text-white" data-testid="tab-cost">
            <DollarSign className="w-3.5 h-3.5 mr-1" /> Cost
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ─────────────────────────── */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OverviewCard title="Equipment Profile" rows={[
              ['Brand', asset.brand],
              ['Model', asset.model],
              ['Serial Number', asset.serial_number, 'mono'],
              ['Location', asset.location],
              ['Area Code', asset.area_code, 'mono'],
            ]} />
            <OverviewCard title="Warranty & AMC" rows={[
              ['Purchase Date', asset.purchase_date, 'date'],
              ['Purchase Cost', asset.purchase_cost ? `₹${Number(asset.purchase_cost).toLocaleString('en-IN')}` : ''],
              ['Vendor', asset.vendor],
              ['AMC Provider', asset.amc_provider],
              ['Warranty Expiry', asset.warranty_expiry, 'date'],
              ['AMC Expiry', asset.amc_expiry, 'date'],
            ]} />
          </div>
          {asset.remarks && (
            <Card className="border-[#E2E8DF] shadow-sm mt-4">
              <CardContent className="py-3 px-4">
                <div className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold mb-1">Remarks</div>
                <div className="text-sm text-[#1A2421] whitespace-pre-wrap">{asset.remarks}</div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Service History (append-only timeline) ─────────────── */}
        <TabsContent value="service">
          <HeadingBand
            title={`Service History · ${history.length} entr${history.length === 1 ? 'y' : 'ies'}`}
            action={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline"
                         onClick={() => exportServiceRecordPdf(API, getHeaders, assetId, asset)}
                         className="bg-white text-[color:var(--color-primary)] hover:bg-white/90 border-white"
                         data-testid="export-service-pdf-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> Export PDF
                </Button>
                <Button size="sm" onClick={() => setAddOpen(true)}
                         className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                         data-testid="add-service-entry-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add entry
                </Button>
              </div>
            }
          />
          {history.length === 0 ? (
            <EmptyState
              icon={Wrench}
              title="No service history yet"
              body="The first completed service (preventive maintenance, breakdown fix, or inspection failure) will appear here as an immutable log entry."
            />
          ) : (
            <ServiceTimeline entries={history} />
          )}
          <AddServiceEntryDialog
            open={addOpen} onOpenChange={setAddOpen}
            assetId={assetId} onSaved={load}
            existing={history} />
        </TabsContent>

        {/* ── Inspections ─────────────────────── */}
        <TabsContent value="inspections">
          <InspectionsTab assetId={assetId} />
        </TabsContent>

        {/* ── PM Schedule ─────────────────────── */}
        <TabsContent value="pm">
          <PMScheduleTab assetId={assetId} />
        </TabsContent>

        {/* ── Documents ─────────────────────────── */}
        <TabsContent value="docs">
          <HeadingBand title="Documents" />
          <EmptyState
            icon={FileText}
            title="Attach warranty cards, invoices, and manuals"
            body="Document upload lands with the media pipeline in Phase B. Existing links can be pasted into the asset's Remarks in the meantime."
          />
        </TabsContent>

        {/* ── Cost stub ─────────────────────────── */}
        <TabsContent value="cost">
          <HeadingBand title="Cost Breakdown" />
          <EmptyState
            icon={DollarSign}
            title="Cost analytics land in Phase C"
            body="Lifetime spend, year-on-year totals, and AMC-vs-breakdown split are computed from Service History once Phase B rolls out."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function OverviewCard({ title, rows }) {
  return (
    <Card className="border-[#E2E8DF] shadow-sm">
      <CardContent className="p-0">
        <div className="px-4 py-2.5 bg-[#F9F9F8] border-b border-[#E2E8DF]">
          <div className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">{title}</div>
        </div>
        <div className="divide-y divide-[#E2E8DF]">
          {rows.map(([k, v, fmt], i) => (
            <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
              <span className="text-[#5C6B64]">{k}</span>
              <span className={`text-[#1A2421] ${fmt === 'mono' ? 'font-mono' : ''}`}>
                {v || <span className="text-[#5C6B64]">—</span>}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, title, body }) {
  return (
    <Card className="border-dashed border-[#E2E8DF] shadow-none">
      <CardContent className="py-12 text-center">
        <Icon className="w-8 h-8 text-[#5C6B64] mx-auto mb-3 opacity-60" />
        <div className="text-sm font-medium text-[#1A2421]">{title}</div>
        <div className="text-xs text-[#5C6B64] mt-1 max-w-md mx-auto">{body}</div>
      </CardContent>
    </Card>
  );
}

// ── Vertical logbook timeline — newest first, append-only ────────────
function ServiceTimeline({ entries }) {
  return (
    <ol className="relative border-l-2 border-[color:var(--color-primary)]/20 ml-2 space-y-4"
        data-testid="service-history-timeline">
      {entries.map((e) => {
        const t = ENTRY_TYPE[e.entry_type] || ENTRY_TYPE.ad_hoc;
        const performed = new Date(e.performed_on);
        const now = new Date();
        const days = Math.round((now - performed) / 86400000);
        const rel = days === 0
          ? 'today'
          : days === 1
            ? 'yesterday'
            : `${days} days ago`;
        return (
          <li key={e.id} className="ml-4" data-testid={`service-entry-${e.id}`}>
            <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-white
                              border-2 border-[color:var(--color-primary)]" />
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <Badge className={`text-[10px] px-1.5 py-0.5 border ${t.tone}`}>{t.label}</Badge>
                    <div className="text-sm font-medium text-[#1A2421] mt-1">
                      {performed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      <span className="text-[11px] text-[#5C6B64] font-normal ml-2">· {rel}</span>
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-[#5C6B64]">
                    {e.cost > 0 && <div className="text-[#1A2421] font-semibold">₹{Number(e.cost).toLocaleString('en-IN')}</div>}
                    {e.duration_minutes ? <div>{e.duration_minutes} min on-site</div> : null}
                  </div>
                </div>
                {e.remarks && <div className="text-sm text-[#1A2421] mt-2 whitespace-pre-wrap">{e.remarks}</div>}
                <div className="text-[11px] text-[#5C6B64] mt-2 flex flex-wrap gap-x-4 gap-y-1">
                  {e.technician && <span>Technician: <span className="text-[#1A2421]">{e.technician}</span></span>}
                  {e.vendor && <span>Vendor: <span className="text-[#1A2421]">{e.vendor}</span></span>}
                  {e.verified_by && <span>Verified by: <span className="text-[#1A2421]">{e.verified_by}</span></span>}
                </div>
                {(e.before_photo || e.after_photo) && (
                  <div className="flex gap-2 mt-2">
                    {e.before_photo && (
                      <a href={e.before_photo} target="_blank" rel="noreferrer">
                        <img src={e.before_photo} alt="before" className="w-16 h-16 rounded object-cover border border-[#E2E8DF]" />
                      </a>
                    )}
                    {e.after_photo && (
                      <a href={e.after_photo} target="_blank" rel="noreferrer">
                        <img src={e.after_photo} alt="after" className="w-16 h-16 rounded object-cover border border-[#E2E8DF]" />
                      </a>
                    )}
                  </div>
                )}
                {e.amends && (
                  <div className="mt-2 text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
                    Amends entry {String(e.amends).slice(0, 8)}…
                  </div>
                )}
              </CardContent>
            </Card>
          </li>
        );
      })}
    </ol>
  );
}

function AddServiceEntryDialog({ open, onOpenChange, assetId, onSaved, existing }) {
  const { getHeaders, API } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyEntry());
  useEffect(() => { if (open) setForm(emptyEntry()); }, [open]);

  const save = async () => {
    if (!form.performed_on) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/maintenance/assets/${assetId}/service-history`,
                        { ...form, cost: Number(form.cost) || 0,
                          duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
                          amends: form.entry_type === 'amendment' ? form.amends : null },
                        { headers: getHeaders() });
      toast.success('Entry logged');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const F = (k) => form[k];
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="add-entry-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A2421]">Add Service Entry</DialogTitle>
          <DialogDescription className="text-[#5C6B64]">
            Entries are append-only. To correct a past entry, choose &quot;Amendment&quot; and link it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Type</Label>
            <Select value={F('entry_type')} onValueChange={(v) => set('entry_type', v)}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="entry-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pm">Preventive Maintenance</SelectItem>
                <SelectItem value="breakdown">Breakdown</SelectItem>
                <SelectItem value="inspection_failure">Inspection Failure</SelectItem>
                <SelectItem value="ad_hoc">Ad-hoc</SelectItem>
                <SelectItem value="amendment">Amendment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Date *</Label>
            <Input type="date" value={F('performed_on') || ''}
                    onChange={(e) => set('performed_on', e.target.value)}
                    className="h-10 border-[#E2E8DF]" data-testid="entry-date-input" />
          </div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Technician</Label><Input value={F('technician')} onChange={(e) => set('technician', e.target.value)} className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Vendor</Label><Input value={F('vendor')} onChange={(e) => set('vendor', e.target.value)} className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Cost (₹)</Label><Input type="number" value={F('cost')} onChange={(e) => set('cost', e.target.value)} className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Duration (min)</Label><Input type="number" value={F('duration_minutes')} onChange={(e) => set('duration_minutes', e.target.value)} className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Verified by</Label><Input value={F('verified_by')} onChange={(e) => set('verified_by', e.target.value)} className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Before Photo URL</Label><Input value={F('before_photo')} onChange={(e) => set('before_photo', e.target.value)} placeholder="https://…" className="h-10 border-[#E2E8DF]" /></div>
          <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">After Photo URL</Label><Input value={F('after_photo')} onChange={(e) => set('after_photo', e.target.value)} placeholder="https://…" className="h-10 border-[#E2E8DF]" /></div>
          {form.entry_type === 'amendment' && (
            <div className="space-y-1 col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Amends entry</Label>
              <Select value={F('amends') || ''} onValueChange={(v) => set('amends', v)}>
                <SelectTrigger className="h-10 border-[#E2E8DF]"><SelectValue placeholder="Select original entry" /></SelectTrigger>
                <SelectContent>{existing.filter((x) => x.entry_type !== 'amendment').map((x) => (
                  <SelectItem key={x.id} value={x.id}>
                    {new Date(x.performed_on).toLocaleDateString()} · {ENTRY_TYPE[x.entry_type]?.label} · {x.technician || '—'}
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1 col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Remarks</Label>
            <Textarea rows={3} value={F('remarks')} onChange={(e) => set('remarks', e.target.value)}
                        className="border-[#E2E8DF]" data-testid="entry-remarks-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E2E8DF]">Cancel</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                  data-testid="entry-save-btn">
            {saving ? 'Saving…' : 'Log entry'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function emptyEntry() {
  return {
    entry_type: 'pm', performed_on: new Date().toISOString().slice(0, 10),
    technician: '', vendor: '', cost: '', duration_minutes: '',
    verified_by: '', remarks: '', before_photo: '', after_photo: '',
    amends: '',
  };
}

// ────────────────────────────────────────────────────────────────────
//  ITER308-B.1b · Inspections Tab
// ────────────────────────────────────────────────────────────────────
function InspectionsTab({ assetId }) {
  const { getHeaders, API } = useAuth();
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [asset, setAsset] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [results, setResults] = useState({});
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [rRes, aRes] = await Promise.all([
      axios.get(`${API}/admin/maintenance/assets/${assetId}/inspections`, { headers: getHeaders() }),
      axios.get(`${API}/admin/maintenance/assets/${assetId}`, { headers: getHeaders() }),
    ]);
    setRuns(rRes.data);
    setAsset(aRes.data);
    const tRes = await axios.get(
      `${API}/admin/maintenance/inspection-templates?category_id=${aRes.data.category_id}`,
      { headers: getHeaders() },
    );
    setTemplates(tRes.data);
  }, [API, assetId, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const template = templates.find((t) => t.id === templateId);
  const submit = async () => {
    if (!template) { toast.error('Pick a template'); return; }
    const items = (template.items || []).map((it) => ({
      key: it.key,
      result: (results[it.key]?.result) || 'na',
      value: results[it.key]?.value ? Number(results[it.key].value) : undefined,
      remark: results[it.key]?.remark || '',
    }));
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/maintenance/assets/${assetId}/inspections`,
        { template_id: templateId, items, remarks: remark },
        { headers: getHeaders() },
      );
      toast.success('Inspection submitted — failures created tickets');
      setResults({}); setRemark(''); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <HeadingBand title={`Inspections · ${runs.length} run${runs.length === 1 ? '' : 's'}`} />
      {templates.length === 0 ? (
        <EmptyState
          icon={Shield} title="No inspection template for this category"
          body="Create an inspection template for this equipment category from the Maintenance hub → Inspection Templates."
        />
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm mb-4">
          <CardContent className="p-4 space-y-3">
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="insp-template-select">
                <SelectValue placeholder="Select inspection template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {template && (
              <div className="space-y-2">
                {(template.items || []).map((it) => (
                  <div key={it.key} className="grid grid-cols-[1fr_120px_140px_1fr] gap-2 items-center">
                    <div className="text-sm text-[#1A2421]">
                      {it.label}
                      {it.is_critical && <span className="ml-1 text-[10px] text-[#C45A41] font-semibold">CRITICAL</span>}
                      {it.unit && <span className="ml-1 text-[11px] text-[#5C6B64]">({it.unit}, {it.min_value}–{it.max_value})</span>}
                    </div>
                    <Select value={results[it.key]?.result || ''}
                            onValueChange={(v) => setResults((p) => ({ ...p, [it.key]: { ...(p[it.key] || {}), result: v } }))}>
                      <SelectTrigger className="h-9 border-[#E2E8DF]" data-testid={`insp-result-${it.key}`}><SelectValue placeholder="Result" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pass">Pass</SelectItem>
                        <SelectItem value="fail">Fail</SelectItem>
                        <SelectItem value="na">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type={it.kind === 'pass_fail' ? 'text' : 'number'}
                            placeholder={it.kind === 'pass_fail' ? 'n/a' : 'Reading'}
                            disabled={it.kind === 'pass_fail'}
                            value={results[it.key]?.value || ''}
                            onChange={(e) => setResults((p) => ({ ...p, [it.key]: { ...(p[it.key] || {}), value: e.target.value } }))}
                            className="h-9 border-[#E2E8DF]" />
                    <Input value={results[it.key]?.remark || ''} placeholder="Remark"
                            onChange={(e) => setResults((p) => ({ ...p, [it.key]: { ...(p[it.key] || {}), remark: e.target.value } }))}
                            className="h-9 border-[#E2E8DF]" />
                  </div>
                ))}
                <Input value={remark} onChange={(e) => setRemark(e.target.value)}
                        placeholder="Overall remarks (optional)"
                        className="h-9 border-[#E2E8DF]" />
                <Button onClick={submit} disabled={saving}
                        className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                        data-testid="insp-submit">
                  {saving ? 'Submitting…' : 'Submit inspection'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {runs.length === 0 ? (
        <EmptyState icon={Shield} title="No inspection runs yet"
                    body="The first run — from a PM task, this tab, or a checklist failure linked to this asset — will appear here." />
      ) : (
        <div className="space-y-2" data-testid="inspection-runs">
          {runs.map((r) => (
            <Card key={r.id} className="border-[#E2E8DF] shadow-sm">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-[#1A2421]">
                      {r.template_name || 'Ad-hoc'}
                      {r.source?.type === 'checklist' &&
                        <Badge className="ml-2 text-[10px] bg-blue-50 text-blue-800 border-blue-200">via Checklist</Badge>}
                    </div>
                    <div className="text-[11px] text-[#5C6B64]">
                      {r.performed_on} · by {r.performed_by || '—'}
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${r.fail_count > 0 ? 'bg-red-50 text-red-800 border-red-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
                    {r.fail_count > 0 ? `${r.fail_count} fail` : 'All pass'}
                  </Badge>
                </div>
                {r.remarks && <div className="text-[11px] text-[#1A2421] mt-1">{r.remarks}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
//  ITER308-B.1b · PM Schedule Tab
// ────────────────────────────────────────────────────────────────────
function PMScheduleTab({ assetId }) {
  const { getHeaders, API } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [asset, setAsset] = useState(null);
  const [freq, setFreq] = useState('weekly');
  const [tplId, setTplId] = useState('');
  const [desc, setDesc] = useState('Preventive maintenance');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [sRes, tRes, aRes] = await Promise.all([
      axios.get(`${API}/admin/maintenance/assets/${assetId}/pm-schedules`, { headers: getHeaders() }),
      axios.get(`${API}/admin/maintenance/tasks?asset_id=${assetId}`, { headers: getHeaders() }),
      axios.get(`${API}/admin/maintenance/assets/${assetId}`, { headers: getHeaders() }),
    ]);
    setSchedules(sRes.data);
    setTasks(tRes.data);
    setAsset(aRes.data);
    const tplRes = await axios.get(
      `${API}/admin/maintenance/inspection-templates?category_id=${aRes.data.category_id}`,
      { headers: getHeaders() },
    );
    setTemplates(tplRes.data);
  }, [API, assetId, getHeaders]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/maintenance/assets/${assetId}/pm-schedules`,
        { asset_id: assetId, frequency: freq,
          inspection_template_id: tplId || null,
          description: desc },
        { headers: getHeaders() },
      );
      toast.success('Schedule created — tasks generate every 15 min');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  const action = async (task, op) => {
    try {
      const body = op === 'complete'
        ? { remarks: 'Completed from asset detail', cost: 0 }
        : op === 'verify'
          ? { remarks: 'Verified from asset detail' }
          : {};
      await axios.post(`${API}/admin/maintenance/tasks/${task.id}/${op}`, body,
                        { headers: getHeaders() });
      toast.success(`Task ${op}ed`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <div>
      <HeadingBand title={`PM Schedules · ${schedules.length} active`} />
      <Card className="border-[#E2E8DF] shadow-sm mb-4">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Frequency</Label>
              <Select value={freq} onValueChange={setFreq}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="pm-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="half_yearly">Half-yearly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Inspection Template</Label>
              <Select value={tplId || 'none'} onValueChange={(v) => setTplId(v === 'none' ? '' : v)}>
                <SelectTrigger className="h-10 border-[#E2E8DF]" data-testid="pm-template-select"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(none)</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">Description</Label>
              <Input value={desc} onChange={(e) => setDesc(e.target.value)}
                      className="h-10 border-[#E2E8DF]" />
            </div>
            <Button onClick={create} disabled={saving}
                    className="h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                    data-testid="pm-create">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add schedule
            </Button>
          </div>
        </CardContent>
      </Card>
      {schedules.length > 0 && (
        <Card className="border-[#E2E8DF] shadow-sm mb-4">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Frequency</th><th className="px-4 py-2.5">Template</th>
                  <th className="px-4 py-2.5">Description</th><th className="px-4 py-2.5">Active</th>
                </tr>
              </thead>
              <tbody data-testid="pm-schedules-list">
                {schedules.map((s, i) => {
                  const t = templates.find((x) => x.id === s.inspection_template_id);
                  return (
                    <tr key={s.id} className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}>
                      <td className="px-4 py-2.5 font-mono text-[12px]">{s.frequency}</td>
                      <td className="px-4 py-2.5">{t?.name || '—'}</td>
                      <td className="px-4 py-2.5">{s.description}</td>
                      <td className="px-4 py-2.5">{s.active ? 'Yes' : 'No'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <HeadingBand title={`Tasks · ${tasks.length} generated`} />
      {tasks.length === 0 ? (
        <EmptyState icon={Calendar} title="No tasks yet"
                    body="After you create a schedule the PM generator will produce tasks within 15 minutes." />
      ) : (
        <Card className="border-[#E2E8DF] shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F9F8]">
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5C6B64]">
                  <th className="px-4 py-2.5">Due</th><th className="px-4 py-2.5">Description</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="pm-tasks-list">
                {tasks.map((t, i) => (
                  <tr key={t.id} className={i > 0 ? 'border-t border-[#E2E8DF]' : ''}
                       data-testid={`pm-task-${t.id}`}>
                    <td className="px-4 py-2.5">{t.due_date}</td>
                    <td className="px-4 py-2.5">{t.description}</td>
                    <td className="px-4 py-2.5">
                      <Badge className="text-[10px] px-1.5 py-0.5 border-emerald-200 bg-emerald-50 text-emerald-800">
                        {t.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 space-x-1">
                      {t.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => action(t, 'start')}
                                className="h-7 text-[11px] border-[#E2E8DF]">Start</Button>
                      )}
                      {(t.status === 'pending' || t.status === 'in_progress') && (
                        <Button size="sm" onClick={() => action(t, 'complete')}
                                className="h-7 text-[11px] bg-emerald-700 text-white">Complete</Button>
                      )}
                      {t.status === 'completed' && (
                        <>
                          <Button size="sm" onClick={() => action(t, 'verify')}
                                  className="h-7 text-[11px] bg-[color:var(--color-primary)] text-white"
                                  data-testid={`verify-${t.id}`}>Verify</Button>
                          <Button size="sm" variant="outline" onClick={() => action(t, 'reject')}
                                  className="h-7 text-[11px] border-[#C45A41] text-[#C45A41]">Reject</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
